import React from 'react';
import {useSelector} from "react-redux";
import {useHistory} from "react-router-dom";
import {Button, Grid, Typography} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import {useActions} from "../../../actions";
import * as AlertActions from "../../../actions/alert";
import * as GiftorActions from "../../../actions/giftor";
import {RootState} from "../../../reducers";
import {RecipientListItem} from "../../../model/giftor";
import Docusign from "../../../pages/client/Kids/Docusign";
import {ageCalculator, FinancialEnum} from "../../../pages/client/Kids/util";
import {Global} from "../../../utils/commonFunc";
import {isDebug, joinAsFullName, noop, sleep, unmaskCurrency} from "../../../utils/commonUtils";
import {GWT_GEN2_STRATEGY} from "../../../constants";


type SendInviteProps = {
    confirmedRecipients: RecipientListItem[];
    handleCancelSend: Function;
    sending: boolean;
    setSending: Function;
};

const SendInvite: React.FC<SendInviteProps> = (props) => {
    const {confirmedRecipients, handleCancelSend, sending, setSending} = props;
    const [openPdf, setOpenPdf] = React.useState(false);
    const [inProgress, setInProgress] = React.useState(false);
    const [documentUrl, setDocumentUrl] = React.useState("");

    const hist = useHistory();
    const alertActions = useActions(AlertActions);
    const gActions = useActions(GiftorActions);

    const authUser = useSelector((state: RootState) => state.auth.user);
    const clientPolicy: any = authUser && authUser.client && authUser && authUser.client.clientPolicies && authUser && authUser.client.clientPolicies[0];

    const createFinalPayloadForSendInvite = (selectedRecipients: RecipientListItem[]): any[] => {
        return selectedRecipients.map(item => ({
            age: ageCalculator(item.dob),
            agentId: clientPolicy && clientPolicy.agentId || clientPolicy.agent.id,
            annualIncome: item.annualIncome ? Number(unmaskCurrency(item.annualIncome)) : 0,
            beginDistributions: item.beginDistribution,
            carrierId: clientPolicy && clientPolicy.carrierId || clientPolicy.carrier.id,
            clientPolicyId: item.clientPolicyId,
            clientType: 0,
            contributionAmount: Number(unmaskCurrency(item.contribution)),
            dob: item.dob,
            documents: [],
            email: item.email,
            endDistributions: item.endDistribution,
            enrollmentId: item.enrollmentId,
            financiallyDependent: item.financiallyDependent === FinancialEnum.Dependent,
            firstName: item.firstName,
            gender: item.gender,
            healthType: item.healthType,
            imo: clientPolicy && clientPolicy.imo && clientPolicy.imo.id,
            lastName: item.lastName,
            otherRelation: item.otherRelation,
            phoneNumber: item.cellNumber,
            relation: item.relation,
            strategyId: GWT_GEN2_STRATEGY,
            tobacco: item.tobacco === 1,
            userId: item.userId,
        }));
    };
    const onCancel = () => {
        handleCancelSend(); // Call the cancel function passed from parent component
    };
    const onSendInvite = async () => {
        setSending(true); // Set working state to true to indicate processing
        const payload = createFinalPayloadForSendInvite(confirmedRecipients); // Create the payload for sending invites
        setInProgress(false); // Reset inProgress state
        setOpenPdf(false); // Reset openPdf state to false
        setDocumentUrl(""); // Reset documentUrl to an empty string

        let res: any = null;

        if (isDebug()) {
            console.log('payload');
            console.info(payload);
            return;
        }

        try {
            res = await gActions.getRecipientDocusign(payload);
        } catch (err) {
            const errMessage = 'Failed to send invite. Please try again later.';
            isDebug() && console.error(errMessage, err);
            setSending(false); // Reset working state on error
            alertActions.setErrorAlert(errMessage);
        }

        if (res && res.statusCode === "200") {
            //@ts-ignore
            Global.set("afterRecipientSigningDocusignFunction", afterRecipientSigningDocusignFunction()) // maybe change name to something appropriate
            setOpenPdf(true)
            setInProgress(false)
            setDocumentUrl(res.url)
        }

        setSending(false);
    };
    const afterRecipientSigningDocusignFunction = () => {
        return async () => {
            let response = await gActions.onRecipientDocusignCompletion();
            if (!!response) {
                setOpenPdf(false)
                const message: string = `Success!
                Your gift invitations have been successfully sent. You'll now be redirected to your home dashboard.`;
                alertActions.setAlert(message, 'success');
                sleep(50).then(() => hist.push('/dashboard'));
            }
        }
    };

    return (
            <Grid container justify="center" spacing={2}>
                <Grid item xs={12}>
                    <Typography component="strong" className="floatLeft w100 mt10 mb20" variant="subtitle1">
                        You are sending invitations to the Greatest Wealth Transfer to the following recipients:
                    </Typography>
                    {
                        confirmedRecipients.map((recipient, index) => (
                                <Typography className="floatLeft w100 mb10" key={index} variant="body2">
                                    {joinAsFullName(recipient)} - {recipient.email}
                                </Typography>
                        ))
                    }
                    <Typography component="strong" className="floatLeft w100 mt10 mb20" variant="subtitle1">
                        Once our admin team has reviewed and approved your invitation, your recipients will receive
                        their invites in their email inbox.
                    </Typography>
                    {
                            isDebug() &&
                            <pre>{JSON.stringify({confirmedRecipients}, null, 2)}</pre>
                    }
                    <Button
                            className="floatLeft mt20"
                            disabled={sending}
                            onClick={onCancel}
                            type="button"
                            size="medium"
                            variant="contained"
                    >
                        Cancel
                    </Button>
                    <Button
                            className="floatRight mt15"
                            color="primary"
                            disabled={sending}
                            onClick={onSendInvite}
                            size="medium"
                            startIcon={<SendIcon/>}
                            variant="contained"
                    >
                        Complete Giftor Documents
                    </Button>
                </Grid>
                {
                        openPdf &&
                        <Docusign
                                openPdf={openPdf}
                                onClose={() => {
                                    sleep().then(noop) // Simulate some delay before closing
                                }}
                                setOpenPdf={setOpenPdf}
                                documentUrl={documentUrl}
                                inProgress={inProgress}
                                onDocSignComplete={() => {
                                    Global.get("afterRecipientSigningDocusignFunction") && Global.get("afterRecipientSigningDocusignFunction")()
                                    Global.clear()
                                }}
                        />
                }
            </Grid>
    );
};

export default SendInvite;
